import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  Stack,
  DialogActions,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/system";
import chatbot from "../../../assets/images/layout/Seal_of_Maharashtra.png";
import ContextShow from "./showContext";
import { Share, VolumeUp, RateReview, Edit, Download } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomMarkdown from "./customMarkdown";
import { optimizeForStreaming, normalizeMarkdown } from "./markdownUtils";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import apiClient, { baseURL } from "../../../services/apiClient";
import FeedbackForm from "./FeedbackForm";
import FeedbackService from "../../../services/feedback.service";
import { useChat } from "./ChatProvider";
import { useToken } from "../../../hooks/useAuth";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const ChatBodyStyled = styled(Box)({
  flex: "1 1 auto",
  backgroundColor: "#f8f9fa",
  padding: "20px 24px",
  overflowY: "auto",
  overflowX: "hidden",
  boxShadow: "none",
  borderRadius: "0",
  minHeight: 0,
  height: "100%",
  position: "relative",
  zIndex: 0,
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(24, 116, 210, 0.3) transparent",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(24, 116, 210, 0.3)",
    borderRadius: "10px",
    border: "2px solid transparent",
    backgroundClip: "content-box",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "rgba(24, 116, 210, 0.5)",
  },
  "@media (max-width: 768px)": {
    padding: "16px 20px",
  },
  "@media (max-width: 600px)": {
    padding: "12px 16px",
  },
  "@media (max-width: 480px)": {
    padding: "10px 12px",
  },
});

// Helper function to extract used context indices from markdown text
const extractUsedContextIndices = (text) => {
  const indices = new Set();
  const regex = /%%\s*(\d+)\s*%%/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    indices.add(Number(match[1]));
  }

  return Array.from(indices).sort((a, b) => a - b);
};

const resolveContextByToken = (contexts, token) => {
  const allContexts = Array.isArray(contexts) ? contexts : [];
  const numericToken = Number(token);
  if (!Number.isFinite(numericToken)) return null;

  const byId = allContexts.find((ctx) => {
    const contextId = Number(ctx?.context_id);
    const contextIdx = Number(ctx?.context_idx);
    const chunkIdx = Number(ctx?.chunk_idx);
    return (
      (Number.isFinite(contextId) && contextId === numericToken) ||
      (Number.isFinite(contextIdx) && contextIdx === numericToken) ||
      (Number.isFinite(chunkIdx) && chunkIdx === numericToken)
    );
  });
  return byId || null;
};

const formatMessageTimestamp = (message) => {
  const rawTimestamp =
    message?.timestamp ||
    message?.created_at ||
    message?.createdAt ||
    message?.time ||
    null;
  if (!rawTimestamp) return "";

  const date = new Date(rawTimestamp);
  if (Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const rawHours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const meridiem = rawHours >= 12 ? "PM" : "AM";
  const hours12 = rawHours % 12 || 12;
  const formattedHours = String(hours12).padStart(2, "0");

  return `${day}-${month}-${year} ${formattedHours}:${minutes}${meridiem}`;
};

const ChatBody = ({ chat, bodyRef, showInlineContext = true }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { chatSessionId, setChatMessages } = useChat();
  const { getToken } = useToken();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [contextModalOpen, setContextModalOpen] = useState(false);
  const [contextModalContent, setContextModalContent] = useState({});
  const [selectedFeedback, setSelectedFeedback] = useState({}); // { messageIndex: "up" | "down" }
  const [showFeedbackForm, setShowFeedbackForm] = useState({}); // { messageIndex: true/false }
  const [autoScroll, setAutoScroll] = useState(true);
  const [expandedThinking, setExpandedThinking] = useState({});
  const [messageFeedback, setMessageFeedback] = useState({}); // { messageIndex: feedbackData }
  const fetchedMessageIds = useRef(new Set()); // Track which message_ids we've already fetched
  const [isSharing, setIsSharing] = useState(false); // Add loading indicator for sharing
  const [shareLoading, setShareLoading] = useState({ whatsapp: false, email: false, native: false });

  const scrollToBottom = () => {
    if (bodyRef?.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (!bodyRef?.current) return;
    const el = bodyRef.current;
    const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    setAutoScroll(isNearBottom);
  };

  useEffect(() => {
    if (chat.length === 0) return;
    const lastMessage = chat[chat.length - 1];
    if (lastMessage?.sender === "user") {
      scrollToBottom();
      return;
    }
    if (autoScroll) {
      scrollToBottom();
    }
  }, [chat, autoScroll]);

  useEffect(() => {
    const newExpandedState = {};
    chat.forEach((message, index) => {
      if (message.sender === "bot" && message.thinking) {
        if (message.text && message.text.trim().length > 0) {
          newExpandedState[index] = false;
        } else if (message.isTyping) {
          newExpandedState[index] = true;
        } else {
          newExpandedState[index] = true;
        }
      }
    });
    setExpandedThinking(newExpandedState);
  }, [chat]);

  useEffect(() => {
    scrollToBottom();
  }, []);

  // Fetch feedback for messages that have message_id
  useEffect(() => {
    const fetchFeedbacks = async () => {
      const feedbackPromises = chat.map(async (message, index) => {
        if (message.sender === "bot" && message.message_id) {
          // Skip if we've already fetched feedback for this message_id
          if (fetchedMessageIds.current.has(message.message_id)) {
            return;
          }

          fetchedMessageIds.current.add(message.message_id);

          try {
            const response = await FeedbackService.getFeedbackByMessageId(message.message_id);
            // Handle nested response structure: response.data.data
            const feedbackData = response?.data?.data || response?.data;
            if (feedbackData) {
              setMessageFeedback((prev) => ({
                ...prev,
                [index]: feedbackData,
              }));
            }
          } catch (err) {
            // If feedback doesn't exist, that's okay
            if (err.response?.status !== 404) {
              console.error("Error fetching feedback for message:", err);
            }
            // Remove from set if fetch failed so we can retry
            fetchedMessageIds.current.delete(message.message_id);
          }
        }
      });
      await Promise.all(feedbackPromises);
    };

    if (chat.length > 0) {
      fetchFeedbacks();
    }
  }, [chat]);

  const openModal = async (messageText, contextArray) => {
    const allContexts = Array.isArray(contextArray) ? contextArray : [];
    // Extract only the contexts that were actually referenced in the message
    const usedIndices = extractUsedContextIndices(messageText || "");
    const usedContexts = usedIndices
      .map((token) => resolveContextByToken(allContexts, token))
      .filter(Boolean);

    setModalData([]);
    setTimeout(() => {
      setModalData(usedContexts);
      setModalOpen(true);
    }, 0);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalData([]);
  };

  const handleContextModalOpen = (context) => {
    if (context) {
      setModalData([context]);
      setModalOpen(true);
      return;
    }
    setContextModalContent({});
    setContextModalOpen(true);
  };

  const handleContextModalClose = () => {
    setContextModalOpen(false);
    setContextModalContent({});
  };

  const handleFeedback = (messageIndex, feedback) => {
    setSelectedFeedback((prev) => ({
      ...prev,
      [messageIndex]: feedback,
    }));
  };

  const handleOpenFeedback = (messageIndex) => {
    setShowFeedbackForm((prev) => ({
      ...prev,
      [messageIndex]: true,
    }));
  };

  const handleCloseFeedback = (messageIndex) => {
    setShowFeedbackForm((prev) => {
      const updated = { ...prev };
      delete updated[messageIndex];
      return updated;
    });
  };

  const handleFeedbackSuccess = async (messageIndex, feedbackData) => {
    // Store message_id and feedback_id in the message object
    if (feedbackData && (feedbackData.message_id || feedbackData.feedback_id)) {
      setChatMessages((prevChat) => {
        const updatedChat = [...prevChat];
        if (updatedChat[messageIndex]) {
          updatedChat[messageIndex] = {
            ...updatedChat[messageIndex],
            message_id: feedbackData.message_id || updatedChat[messageIndex].message_id,
            feedback_id: feedbackData.feedback_id || updatedChat[messageIndex].feedback_id,
          };
        }
        return updatedChat;
      });

      // Fetch updated feedback to display
      if (feedbackData.message_id) {
        try {
          const response = await FeedbackService.getFeedbackByMessageId(feedbackData.message_id);
          // Handle nested response structure: response.data.data
          const updatedFeedbackData = response?.data?.data || response?.data;
          if (updatedFeedbackData) {
            setMessageFeedback((prev) => ({
              ...prev,
              [messageIndex]: updatedFeedbackData,
            }));
          }
        } catch (err) {
          console.error("Error fetching updated feedback:", err);
        }
      }
    }

    // The form will show success message and auto-close
    setTimeout(() => {
      handleCloseFeedback(messageIndex);
    }, 2000);
  };

  // Helper to get user message (question) for a bot message
  const getQuestionForBotMessage = (botMessageIndex) => {
    // Find the previous user message
    for (let i = botMessageIndex - 1; i >= 0; i--) {
      if (chat[i]?.sender === "user") {
        return chat[i].text || "";
      }
    }
    return "";
  };

  // Helper to extract user_id from token (if available) or use default
  const getUserId = () => {
    // Try to extract user info from token if possible
    // For now, using a placeholder - can be enhanced later
    try {
      const token = getToken();
      if (token) {
        // You can decode JWT token here if it contains user_id
        // const decoded = jwt_decode(token);
        // return decoded.user_id || decoded.sub || "anonymous";
      }
    } catch (e) {
      console.error("Error extracting user_id:", e);
    }
    return "anonymous";
  };

  const textToSpeech = (text) => {
    // Remove context markers before sending to TTS
    const cleanText = text.replace(/%%\s*\d+\s*%%/g, "");

    apiClient
      .post("/chatbot/text_to_speech", { text: cleanText })
      .then((res) => {
        if (res.data && res.data.message) {
          const audio = new Audio(res.data.message);
          audio
            .play()
            .catch((error) => console.error("Audio play error:", error));
        }
      })
      .catch((error) => {
        console.error("Text-to-Speech API Error:", error);
      });
  };

  const watermarkText = "This is an AI generated report.";
  const watermarkColor = "#CCCCCC";
  const watermarkFontSize = 20;

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogIndex, setShareDialogIndex] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const contentRefs = useRef({});

  const generatePDF = async (index) => {
    const ref = contentRefs.current[index];
    if (!ref) return null;
    try {
      const canvas = await html2canvas(ref, {
        scale: 2,
        useCORS: true,
        logging: false,
        ignoreElements: (element) =>
          element.classList && element.classList.contains("exclude-from-pdf"),
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = canvas.width;
      const contentHeight = canvas.height;
      const aspectRatio = contentWidth / contentHeight;
      let pdfContentWidth = pageWidth - 20;
      let pdfContentHeight = pdfContentWidth / aspectRatio;
      if (pdfContentHeight > pageHeight - 20) {
        pdfContentHeight = pageHeight - 20;
        pdfContentWidth = pdfContentHeight * aspectRatio;
      }
      const contentX = (pageWidth - pdfContentWidth) / 2;
      const contentY = 20;
      // Watermark
      const showWatermark = true;
      if (showWatermark) {
        const watermarkX = pageWidth / 2;
        const watermarkY = 15;
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(watermarkFontSize);
        pdf.setTextColor(watermarkColor);
        pdf.text(watermarkText, watermarkX, watermarkY, { align: "center" });
      }
      pdf.addImage(imgData, "PNG", contentX, contentY, pdfContentWidth, pdfContentHeight);
      return pdf;
    } catch (error) {
      console.error("Error generating PDF:", error);
      return null;
    }
  };

  const handleDownload = async (index) => {
    const pdf = await generatePDF(index);
    if (pdf) {
      pdf.save(`response_${index}.pdf`);
    }
  };

  const handleShare = async (index, method) => {
    setShareLoading((prev) => ({ ...prev, [method]: true }));
    const answerText = chat[index]?.text || '';
    // Find the last user message before this bot message
    let questionText = '';
    for (let i = index - 1; i >= 0; i--) {
      if (chat[i]?.sender === 'user') {
        questionText = chat[i].text || '';
        break;
      }
    }
    const fullText = (questionText ? `Question: ${questionText}\n` : '') + `Answer: ${answerText}`;
    console.log('PAYLOAD SENT TO PDF API:', { data: fullText });
    if (!answerText) {
      setShareLoading((prev) => ({ ...prev, [method]: false }));
      return;
    }
    try {
      const response = await apiClient.post(
        `${baseURL}/section_page/get_pdf_report2`,
        { data: fullText }
      );
      const urlFromApi = response.data?.path;
      setPdfUrl(urlFromApi);
      if (!urlFromApi) {
        console.error("No URL returned from PDF API");
        setShareLoading((prev) => ({ ...prev, [method]: false }));
        return;
      }
      switch (method) {
        case "native":
          try {
            await navigator.share({
              title: "PDF Document",
              text: `Check out this PDF document. You can view it here: ${urlFromApi}`,
            });
          } catch (error) {
            console.error("Native sharing error:", error);
            setShareDialogOpen(true);
          }
          break;
        case "whatsapp": {
          const whatsappMsg = `Hi,%0aPlease check out this PDF document at the following link:%0a${urlFromApi}%0aYou can view it using the link above.`;
          window.open(
            `https://wa.me/?text=${whatsappMsg}`,
            "_blank"
          );
          break;
        }
        case "email": {
          const subject = encodeURIComponent("PDF Document");
          const body = encodeURIComponent(
            `Hi,\n\nPlease check out this PDF document at the following link:\n${urlFromApi}\n\nYou can view it using the link above.`
          );
          const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
          const link = document.createElement("a");
          link.href = mailtoLink;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          break;
        }
      }
    } catch (err) {
      console.error("Error during PDF sharing or API:", err);
    }
    setShareLoading((prev) => ({ ...prev, [method]: false }));
  };

  const handleOpenShareDialog = (index) => {
    setShareDialogIndex(index);
    setShareDialogOpen(true);
  };
  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setShareDialogIndex(null);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  };

  return (
    <ChatBodyStyled className="chat-body" ref={bodyRef} onScroll={handleScroll}>
      {chat.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            mt: 3,
            mb: 2,
          }}
        >
          <Avatar
            alt="Bot Avatar"
            src={chatbot}
            variant="square"
            sx={{
              width: "40px",
              height: "40px",
              mr: 2,
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(24, 116, 210, 0.15)",
              border: "2px solid rgba(24, 116, 210, 0.1)",
            }}
          />
          <Box
            sx={{
              backgroundColor: "#ffffff",
              color: "#2c3e50",
              borderRadius: "16px",
              p: 3,
              maxWidth: "80%",
              textAlign: "left",
              border: "1px solid rgba(24, 116, 210, 0.12)",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
              lineHeight: 1.6,
            }}
          >
            <strong>{t("chatBody.welcomeTitle")}</strong>
            <br />
            <br />
            {t("chatBody.introMsg")}
            <br />
            <br />
            {t("chatBody.purposeMsg")}
            <br />
            <br />
            {t("chatBody.promptMsg")}
          </Box>
        </Box>
      ) : (
        chat.map((message, index) => {
          const messageTimestamp = formatMessageTimestamp(message);
          // Calculate used contexts for each message
          const usedContextIndices =
            message.sender === "bot" && message.text
              ? extractUsedContextIndices(message.text)
              : [];

          const hasUsedContexts =
            usedContextIndices.length > 0 ||
            (Array.isArray(message.contextArray) &&
              message.contextArray.length > 0);

          return (
            <Box key={index} sx={{ mb: 2 }}>
              {message.sender === "bot" ? (
                <Box sx={{ display: "flex", mb: 2, pl: 0 }}>
                  <Avatar
                    alt="Bot Avatar"
                    src={chatbot}
                    variant="square"
                    sx={{
                      width: { xs: "32px", sm: "34px", md: "36px" },
                      height: { xs: "32px", sm: "34px", md: "36px" },
                      mr: { xs: 1.5, sm: 1.75, md: 2 },
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(24, 116, 210, 0.12)",
                      border: "1px solid rgba(24, 116, 210, 0.08)",
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    className="chat-message"
                    sx={{
                      backgroundColor: "#ffffff",
                      color: "#2c3e50",
                      borderRadius: "16px",
                      p: { xs: 1.5, sm: 2, md: 2.5 },
                      maxWidth: { xs: "90%", sm: "85%", md: "80%" },
                      width: "fit-content",
                      border: "1px solid rgba(24, 116, 210, 0.12)",
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    {message.thinking && !message.isTyping && (
                      <Box sx={{ mb: 1.5 }}>
                        <Accordion
                          elevation={0}
                          expanded={expandedThinking[index] || false}
                          onChange={(e, isExpanded) => {
                            setExpandedThinking((prev) => ({
                              ...prev,
                              [index]: isExpanded,
                            }));
                          }}
                          sx={{
                            backgroundColor: "#fafbfd",
                            border: "1px solid rgba(24, 116, 210, 0.08)",
                            borderRadius: "10px",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={
                              <ExpandMoreIcon sx={{ fontSize: 18 }} />
                            }
                            sx={{
                              minHeight: 0,
                              "& .MuiAccordionSummary-content": { my: 0.5 },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "#002855", fontWeight: 600 }}
                            >
                              Thinking (context synthesis)
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <CustomMarkdown
                              markdown={optimizeForStreaming(
                                normalizeMarkdown(message.thinking)
                              )}
                              contextArray={[]}
                              handleContextModalOpen={() => { }}
                              showInlineContext={false}
                            />
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}
                    {message.isTyping ? (
                      <Box sx={{ display: "inline-flex", gap: 0.5 }}>
                        <span className="dot" />
                        <span className="dot" />
                        <span className="dot" />
                      </Box>
                    ) : (
                      <Box ref={el => contentRefs.current[index] = el} sx={{}}>
                        <CustomMarkdown
                          markdown={optimizeForStreaming(
                            normalizeMarkdown(message.text)
                          )}
                          contextArray={message.contextArray || []}
                          handleContextModalOpen={handleContextModalOpen}
                          showInlineContext={showInlineContext}
                        />
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 0.25, sm: 0.4, md: 0.5 },
                        mt: { xs: 1.5, sm: 1.75, md: 2 },
                        pt: { xs: 0.75, sm: 0.875, md: 1 },
                        borderTop: "1px solid rgba(24, 116, 210, 0.08)",
                        flexWrap: "wrap",
                      }}
                    >
                      {hasUsedContexts && (
                        <Button
                          onClick={() =>
                            openModal(message.text, message.contextArray)
                          }
                          size="small"
                          sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            px: { xs: 1.5, sm: 1.75, md: 2 },
                            py: { xs: 0.4, sm: 0.45, md: 0.5 },
                            fontSize: {
                              xs: "0.7rem",
                              sm: "0.75rem",
                              md: "0.8rem",
                            },
                            fontWeight: 500,
                            backgroundColor: "rgba(24, 116, 210, 0.08)",
                            color: "rgba(24, 116, 210, 1)",
                            border: "1px solid rgba(24, 116, 210, 0.12)",
                            "&:hover": {
                              backgroundColor: "rgba(24, 116, 210, 0.12)",
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 8px rgba(24, 116, 210, 0.2)",
                            },
                          }}
                        >
                          Show Context
                        </Button>
                      )}

                      {message.isDone && !message.isTyping && !showFeedbackForm[index] && (
                        <>
                          <Box
                            sx={{
                              mt: 0.5,
                              p: 0.5,
                              backgroundColor: "rgba(24, 116, 210, 0.05)",
                              borderRadius: "8px",
                              border: "1px solid rgba(24, 116, 210, 0.12)",
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end", // right-align the icon
                            }}
                          >
                            <IconButton
                              onClick={() => handleOpenFeedback(index)}
                              size="small"
                              sx={{
                                color: "rgba(24, 116, 210, 1)",
                                backgroundColor: "rgba(24, 116, 210, 0.08)",
                                borderRadius: "6px",
                                p: { xs: 0.3, sm: 0.35, md: 0.35 },
                                "&:hover": {
                                  backgroundColor: "rgba(24, 116, 210, 0.12)",
                                  transform: "translateY(-1px)",
                                  boxShadow: "0 2px 8px rgba(24, 116, 210, 0.2)",
                                },
                              }}
                              title={message.message_id ? "Update Feedback" : "Add Feedback"}
                            >
                              {message.message_id ? (
                                <Edit sx={{ fontSize: { xs: "12px", sm: "13px", md: "14px" } }} />
                              ) : (
                                <RateReview sx={{ fontSize: { xs: "12px", sm: "13px", md: "14px" } }} />
                              )}
                            </IconButton>

                          </Box>
                        </>
                      )}
                      <IconButton
                        size="small"
                        sx={{
                          color:
                            selectedFeedback[index] === "up"
                              ? "rgba(24, 116, 210, 1)"
                              : "#666666",
                          backgroundColor:
                            selectedFeedback[index] === "up"
                              ? "rgba(24, 116, 210, 0.08)"
                              : "transparent",
                          borderRadius: "8px",
                          p: { xs: 0.4, sm: 0.45, md: 0.5 },
                          "&:hover": {
                            backgroundColor:
                              selectedFeedback[index] === "up"
                                ? "rgba(24, 116, 210, 0.12)"
                                : "rgba(0, 0, 0, 0.04)",
                            transform: "translateY(-1px)",
                          },
                        }}
                        onClick={() => handleFeedback(index, "up")}
                      >
                        <ThumbUpIcon
                          sx={{
                            fontSize: { xs: "14px", sm: "15px", md: "16px" },
                          }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{
                          color:
                            selectedFeedback[index] === "down" ? "#d32f2f" : "#666666",
                          backgroundColor:
                            selectedFeedback[index] === "down"
                              ? "rgba(211, 47, 47, 0.08)"
                              : "transparent",
                          borderRadius: "8px",
                          p: { xs: 0.4, sm: 0.45, md: 0.5 },
                          "&:hover": {
                            backgroundColor:
                              selectedFeedback[index] === "down"
                                ? "rgba(211, 47, 47, 0.12)"
                                : "rgba(0, 0, 0, 0.04)",
                            transform: "translateY(-1px)",
                          },
                        }}
                        onClick={() => handleFeedback(index, "down")}
                      >
                        <ThumbDownIcon
                          sx={{
                            fontSize: { xs: "14px", sm: "15px", md: "16px" },
                          }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{
                          color: "#666666",
                          borderRadius: "8px",
                          p: { xs: 0.4, sm: 0.45, md: 0.5 },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            transform: "translateY(-1px)",
                          },
                        }}
                        onClick={() => textToSpeech(message.text)}
                      >
                        <VolumeUp
                          sx={{
                            fontSize: { xs: "14px", sm: "15px", md: "16px" },
                          }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{
                          color: "#666666",
                          borderRadius: "8px",
                          p: { xs: 0.4, sm: 0.45, md: 0.5 },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            transform: "translateY(-1px)",
                          },
                        }}
                        onClick={() => handleDownload(index)}
                      >
                        <Download sx={{ fontSize: { xs: "14px", sm: "15px", md: "16px" } }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{
                          color: "#666666",
                          borderRadius: "8px",
                          p: { xs: 0.4, sm: 0.45, md: 0.5 },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            transform: "translateY(-1px)",
                          },
                        }}
                        onClick={() => handleOpenShareDialog(index)}
                      >
                        <Share sx={{ fontSize: { xs: "14px", sm: "15px", md: "16px" } }} />
                      </IconButton>
                    </Box>
                    {/* Feedback area BELOW action row */}
                    {message.isDone && !message.isTyping && !showFeedbackForm[index] && (
                      <Box
                      >

                        {messageFeedback[index]?.additional_comments && (

                          <Box
                            sx={{
                              mt: 0.5,
                              p: 0.5,
                              backgroundColor: "rgba(24, 116, 210, 0.05)",
                              borderRadius: "8px",
                              border: "1px solid rgba(24, 116, 210, 0.12)",
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                            }}
                          >

                            <Box sx={{ mt: 0.5, alignSelf: "stretch" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "#000000",
                                  fontSize: "0.9rem",
                                  fontWeight: 900,
                                  mb: 0.25,
                                  letterSpacing: "0.05rem",
                                }}
                              >
                                Feedback:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#333333",
                                  fontSize: "0.8rem",
                                  fontWeight: 400,
                                  lineHeight: 1.4,
                                  wordBreak: "break-word",
                                }}
                              >
                                {messageFeedback[index].additional_comments}
                              </Typography>
                            </Box>

                          </Box>
                        )}
                      </Box>
                    )}
                    {message.isDone && !message.isTyping && showFeedbackForm[index] && (
                      <FeedbackForm
                        messageIndex={index}
                        question={getQuestionForBotMessage(index)}
                        answer={message.text || ""}
                        contexts={message.contextArray || []}
                        feedbackType={selectedFeedback[index] || "neutral"}
                        sessionId={chatSessionId || ""}
                        userId={getUserId()}
                        messageId={message.message_id}
                        onClose={() => handleCloseFeedback(index)}
                        onSuccess={(feedbackData) => handleFeedbackSuccess(index, feedbackData)}
                      />
                    )}
                    {messageTimestamp && (
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1,
                          display: "block",
                          color: "rgba(44, 62, 80, 0.65)",
                          fontSize: { xs: "11px", sm: "12px", md: "13px" },
                        }}
                      >
                        {messageTimestamp}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 2,
                    pr: 0,
                  }}
                >
                  <Box
                    className="chat-message"
                    sx={{
                      backgroundColor: "rgba(24, 116, 210, 0.9)",
                      color: "#ffffff",
                      borderRadius: "16px",
                      p: { xs: 1.5, sm: 2, md: 2.5 },
                      maxWidth: { xs: "90%", sm: "85%", md: "80%" },
                      width: "fit-content",
                      boxShadow: "0 6px 20px rgba(24, 116, 210, 0.25)",
                      border: "1px solid rgba(24, 116, 210, 0.3)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        boxShadow: "0 8px 24px rgba(24, 116, 210, 0.35)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#ffffff",
                        fontSize: {
                          xs: "0.85rem",
                          sm: "0.9rem",
                          md: "0.95rem",
                        },
                        lineHeight: 1.5,
                        wordBreak: "break-word",
                      }}
                    >
                      {message.text}
                    </Typography>
                    {messageTimestamp && (
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1,
                          display: "block",
                          color: "rgba(255, 255, 255, 0.85)",
                          fontSize: { xs: "11px", sm: "12px", md: "13px" },
                          textAlign: "right",
                        }}
                      >
                        {messageTimestamp}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })
      )}

      {/* Context Modal */}
      <Dialog
        open={contextModalOpen}
        onClose={handleContextModalClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            backgroundColor: "#f8f9fa",
            borderRadius: { xs: 0, sm: "16px" },
            boxShadow:
              "0 20px 60px rgba(0, 0, 0, 0.12), 0 8px 25px rgba(0, 0, 0, 0.08)",
            margin: { xs: 0, sm: "32px" },
            maxHeight: { xs: "100vh", sm: "90vh" },
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f1f3f4",
            color: "#002855",
            fontWeight: 600,
            borderBottom: "1px solid rgba(24, 116, 210, 0.12)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Context Information
          <IconButton
            onClick={handleContextModalClose}
            sx={{
              color: "#666666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                color: "#333333",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#f8f9fa", p: 0 }}>
          <Box p={3}>
            {contextModalContent?.context ? (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid rgba(24, 116, 210, 0.12)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                }}
              >
                {contextModalContent.context}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid rgba(24, 116, 210, 0.12)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                  color: "#666666",
                  fontStyle: "italic",
                }}
              >
                No context available.
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Document Context Modal */}
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            backgroundColor: "#f8f9fa",
            borderRadius: { xs: 0, sm: "16px" },
            boxShadow:
              "0 20px 60px rgba(0, 0, 0, 0.12), 0 8px 25px rgba(0, 0, 0, 0.08)",
            margin: { xs: 0, sm: "32px" },
            maxHeight: { xs: "100vh", sm: "90vh" },
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f1f3f4",
            color: "#002855",
            fontWeight: 600,
            borderBottom: "1px solid rgba(24, 116, 210, 0.12)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Referenced Context
          <IconButton
            onClick={closeModal}
            sx={{
              color: "#666666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                color: "#333333",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#f8f9fa", p: 0 }}>
          <ContextShow
            key={modalData.length}
            contextData={{ sources: modalData }}
          />
        </DialogContent>
      </Dialog>

      {/* Share PDF Dialog */}
      <Dialog open={shareDialogOpen} onClose={handleCloseShareDialog}>
        <DialogTitle>Share PDF</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Button variant="contained" color="success" onClick={() => handleShare(shareDialogIndex, "whatsapp")} disabled={shareLoading.whatsapp}>
              {shareLoading.whatsapp ? "Sharing..." : "Share via WhatsApp"}
            </Button>
            <Button variant="contained" color="primary" onClick={() => handleShare(shareDialogIndex, "email")} disabled={shareLoading.email}>
              {shareLoading.email ? "Sharing..." : "Share via Email"}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareDialog} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </ChatBodyStyled>
  );
};

export default ChatBody;
